"use client";
import { hasWindow } from "@lib/utils/hasWindow";
import { Box } from "@ecco/ui/box";
import StarsRating from "@ecco/ui/stars-rating";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { Attributes } from "@lib/enums/ProductAttributes";
import { useYotpoReviews } from "./useYotpoReviews";
import { activeSiteCurrency } from "@config/site/site-config";
import { useLocale } from "next-intl";
import type { Product } from "@lib/product/types";

declare const window: Window &
    typeof globalThis & {
        yotpoWidgetsContainer?: Yotpo;
    };

interface Yotpo {
    guids: {
        [key: string]: { initWidgets: () => void };
    };
}

export const REVIEWS_SECTION_ID = "reviews-section";

// Dispatched on the reviews section to load Yotpo before it scrolls into view.
export const LOAD_REVIEWS_EVENT = "ecco:load-reviews";

export const YotpoReviewsWidget = ({
    product,
    totalScore,
}: {
    product: Product;
    totalScore: number | undefined;
}) => {
    const locale = useLocale();
    const { get: getMicrocopy } = useMicrocopy();

    const articleNumber = getProductAttributeValue(product.attributes, Attributes.articleNumber);

    const { yotpoInstanceId, yotpoAppId } = useYotpoReviews();

    const yotpoWidgetInstance = window?.yotpoWidgetsContainer?.guids?.[yotpoAppId];

    const sectionRef = useRef<HTMLDivElement | null>(null);
    const [shouldLoad, setShouldLoad] = useState(false);

    useEffect(() => {
        if (shouldLoad) return;

        const el = sectionRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setShouldLoad(true);
            return;
        }

        const load = () => setShouldLoad(true);
        el.addEventListener(LOAD_REVIEWS_EVENT, load);

        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setShouldLoad(true);
                }
            },
            { rootMargin: "200px 0px" }
        );
        io.observe(el);

        return () => {
            el.removeEventListener(LOAD_REVIEWS_EVENT, load);
            io.disconnect();
        };
    }, [shouldLoad]);

    useEffect(() => {
        let timerId: string | number | NodeJS.Timeout;
        if (yotpoWidgetInstance && typeof yotpoWidgetInstance?.initWidgets === "function") {
            timerId = setTimeout(() => yotpoWidgetInstance?.initWidgets(), 300);
        }
        return () => {
            clearTimeout(timerId);
        };
    }, [yotpoWidgetInstance, product.id, locale]);

    const getVariantPrice = (variants: typeof product.variants) => {
        const variantWithPrice = variants.findLast((it) => it?.price?.value?.centAmount);
        return variantWithPrice ? variantWithPrice.price.value.centAmount / 100 : 0;
    };

    return (
        <>
            {shouldLoad && (
                <Script
                    src={`https://cdn-widgetsrepository.yotpo.com/v1/loader/${yotpoAppId}`}
                    async
                />
            )}
            {!totalScore && (
                <Box
                    paddingLeft={6}
                    display="flex"
                    flexDirection="row"
                    gap={2}
                    fontSize="24px"
                    color="gray.700"
                    marginY={8}
                >
                    <StarsRating score={0} size={"sm"} />0{" "}
                    {getMicrocopy(CommonCMS.page, CommonCMS.PDP.reviews)?.toLowerCase()}
                </Box>
            )}
            <Box
                ref={sectionRef}
                id={REVIEWS_SECTION_ID}
                className="yotpo-widget-instance"
                data-yotpo-instance-id={yotpoInstanceId}
                data-yotpo-product-id={articleNumber}
                data-yotpo-name={product.name}
                data-yotpo-url={hasWindow ? window.location.href : ""}
                data-yotpo-image-url={product.media.find((item) => item.type === "image")?.url}
                data-yotpo-price={getVariantPrice(product.variants)}
                data-yotpo-currency={activeSiteCurrency}
                data-yotpo-description={product.description}
                mb={[0, 0, 10]}
            />
        </>
    );
};

export default YotpoReviewsWidget;
